import { useEffect, useState } from 'react';
import type { SocialMedia } from '../../lib/workflowsApi';

export interface PlatformSelection {
  /** Platform the enrichment payload must use. Always the prop when locked. */
  socialMedia: SocialMedia;
  /** True when the entry point pre-selects and locks the platform (LinkedIn). */
  isPlatformLocked: boolean;
  /** Updates the user-chosen platform; ignored by `socialMedia` while locked. */
  selectPlatform: (value: SocialMedia) => void;
}

/**
 * FLOSEAI-107 — platform selection for the enrichment form.
 *
 * The LinkedIn enrichment entry point pre-selects and LOCKS the platform; other
 * entry points (e.g. Instagram) keep it selectable. `socialMedia` is the value
 * the payload must carry: for the locked flow it is always the prop (defense in
 * depth — never trust transient UI state).
 *
 * `entryPlatform` is the platform supplied by the entry point. Because the form
 * is mounted once and reopened from different entry points, a useEffect re-syncs
 * the selection when it changes — otherwise a LinkedIn → Instagram switch would
 * keep the stale value (wrong URL validation, placeholder and progress steps).
 */
export function usePlatformSelection(entryPlatform: SocialMedia): PlatformSelection {
  const isPlatformLocked = entryPlatform === 'LinkedIn';
  const [selectedSocialMedia, setSelectedSocialMedia] = useState<SocialMedia>(entryPlatform);

  useEffect(() => {
    setSelectedSocialMedia(entryPlatform);
  }, [entryPlatform]);

  return {
    socialMedia: isPlatformLocked ? entryPlatform : selectedSocialMedia,
    isPlatformLocked,
    selectPlatform: setSelectedSocialMedia,
  };
}
